import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAdoption, useSetAdoption } from '@/api/adoption'
import type { Adoption, AdoptionBody } from '@/api/adoption'
import { useResetDemo } from '@/api/demo'
import { useIdempotencyKey } from '@/api/idempotency'
import { useListingSetting, useUpdateListingSetting } from '@/api/listings'
import type { ApiError } from '@/api/me'
import { useWorkspace } from '@/api/workspace'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Workspace settings (UI-SCREENS A-11): the adoption date (#48) and the Listing brand (#317),
// Owner only.
export const Route = createFileRoute('/w/$slug/settings/workspace')({
  component: WorkspaceSettings,
  staticData: { breadcrumb: 'settings.workspace.title' },
})

function WorkspaceSettings() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  // Already loaded by the workspace layout; Staff has no settings screens (the API refuses too).
  const workspace = useWorkspace(slug)
  if (workspace.data?.role !== 'owner') return <Navigate to="/w/$slug" params={{ slug }} replace />

  return (
    <section className="flex max-w-xl flex-col gap-4">
      <h1 className="font-heading text-xl font-semibold">{t('settings.workspace.title')}</h1>
      <AdoptionCard slug={slug} />
      <ListingBrandCard slug={slug} />
      {workspace.data.demo ? <DemoResetCard slug={slug} /> : null}
    </section>
  )
}

// The brand every Product's marketplace Listing shows (#317), e.g. "Risal 3D" or "No brand".
function ListingBrandCard({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const setting = useListingSetting(slug)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('settings.listingBrand.title')}</CardTitle>
        <CardDescription>{t('settings.listingBrand.explanation')}</CardDescription>
      </CardHeader>
      <CardContent>
        {setting.isPending ? (
          <LoadingSkeleton variant="form" rows={2} />
        ) : !setting.data ? (
          <p role="alert" className="text-sm text-destructive">
            {t('errors.generic')}
          </p>
        ) : (
          <ListingBrandForm key={setting.data.listing_brand ?? ''} slug={slug} brand={setting.data.listing_brand} />
        )}
      </CardContent>
    </Card>
  )
}

function ListingBrandForm({ slug, brand }: { slug: string; brand: string | null }) {
  const { t } = useTranslation()
  const update = useUpdateListingSetting()
  const form = useForm<{ brand: string }>({
    resolver: zodResolver(z.object({ brand: z.string().max(100, { message: t('settings.listingBrand.tooLong') }) })),
    defaultValues: { brand: brand ?? '' },
  })
  const onSubmit = form.handleSubmit(({ brand: value }) =>
    update.mutate({ params: { path: { slug } }, body: { listing_setting: { listing_brand: value.trim() || null } } }),
  )

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      <div className="flex flex-col gap-2">
        <Label htmlFor="listing-brand">{t('settings.listingBrand.label')}</Label>
        <Input id="listing-brand" className="h-11" maxLength={100} autoComplete="off" {...form.register('brand')} />
        <p role="alert" className="text-sm text-destructive empty:hidden">
          {form.formState.errors.brand?.message ?? (update.isError ? t('errors.generic') : '')}
        </p>
      </div>
      <Button type="submit" className="h-11 self-start" disabled={update.isPending}>
        {t(update.isSuccess && !form.formState.isDirty ? 'settings.listingBrand.saved' : 'settings.listingBrand.save')}
      </Button>
    </form>
  )
}

// Reset = replace (09 Onboarding → Demo workspace): the server creates a fresh Demo workspace with
// a new slug and archives this one, so the SPA moves to the new slug.
function DemoResetCard({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const resetDemo = useResetDemo()

  function onConfirm() {
    resetDemo.mutate(
      { params: { path: { slug } } },
      { onSuccess: (data) => navigate({ to: '/w/$slug', params: { slug: data.slug }, replace: true }) },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('demo.reset.title')}</CardTitle>
        <CardDescription>{t('demo.reset.explanation')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p role="alert" className="text-sm text-destructive empty:hidden">
          {resetDemo.isError ? t('errors.generic') : ''}
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="h-11 self-start" disabled={resetDemo.isPending}>
              {t(resetDemo.isPending ? 'demo.reset.resetting' : 'demo.reset.button')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('demo.reset.confirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('demo.reset.confirmBody')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="h-11">{t('demo.reset.cancel')}</AlertDialogCancel>
              <AlertDialogAction className="h-11" onClick={onConfirm}>
                {t('demo.reset.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}

function AdoptionCard({ slug }: { slug: string }) {
  const { t } = useTranslation()
  const adoption = useAdoption(slug)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('settings.adoption.title')}</CardTitle>
        <CardDescription>{t('settings.adoption.explanation')}</CardDescription>
      </CardHeader>
      <CardContent>
        {adoption.isPending ? (
          <LoadingSkeleton variant="form" rows={4} />
        ) : adoption.isError || !adoption.data ? (
          <p role="alert" className="text-sm text-destructive">
            {t('errors.generic')}
          </p>
        ) : adoption.data.locked || !adoption.data.can.update ? (
          <LockedAdoption adoption={adoption.data} />
        ) : (
          <AdoptionForm slug={slug} adoption={adoption.data} />
        )}
      </CardContent>
    </Card>
  )
}

// 2026-09-01 → "1 September 2026". The date has no time, so it is formatted as UTC.
function formatDate(iso: string) {
  return new Intl.DateTimeFormat('id-ID', { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
}

function LockedAdoption({ adoption }: { adoption: Adoption }) {
  const { t } = useTranslation()

  return (
    <dl className="flex flex-col gap-2">
      <dt className="text-sm font-medium">{t('settings.adoption.label')}</dt>
      <dd className="text-base">{adoption.adopted_on ? formatDate(adoption.adopted_on) : t('settings.adoption.empty')}</dd>
      <dd className="text-sm text-muted-foreground">{t('settings.adoption.locked')}</dd>
    </dl>
  )
}

function AdoptionForm({ slug, adoption }: { slug: string; adoption: Adoption }) {
  const { t } = useTranslation()
  const setAdoption = useSetAdoption()
  const idempotency = useIdempotencyKey()

  // Empty clears the date. Today is the Workspace's today, from the server.
  const formSchema = z.object({
    adoptedOn: z.string().refine((value) => value === '' || value <= adoption.today, {
      message: t('settings.adoption.inFuture'),
    }),
  })
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { adoptedOn: adoption.adopted_on ?? '' },
  })

  const onSubmit = form.handleSubmit(({ adoptedOn }) => {
    const body: AdoptionBody = { adoption: { adopted_on: adoptedOn === '' ? null : adoptedOn } }
    setAdoption.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) => form.reset({ adoptedOn: data.adopted_on ?? '' }),
        onSettled: (_data, error) => idempotency.settle(error),
      },
    )
  })

  const fieldError = form.formState.errors.adoptedOn?.message ?? ''
  const requestError = setAdoption.isError ? errorMessage(t, setAdoption.error) : ''

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="status" aria-live="polite" className="text-sm empty:hidden">
        {setAdoption.isSuccess && !form.formState.isDirty ? t('settings.adoption.saved') : ''}
      </p>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {requestError}
      </p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="adopted-on">{t('settings.adoption.label')}</Label>
        <Input
          id="adopted-on"
          type="date"
          className="h-11"
          max={adoption.today}
          aria-invalid={Boolean(fieldError)}
          aria-describedby="adopted-on-hint adopted-on-error"
          {...form.register('adoptedOn')}
        />
        <p id="adopted-on-hint" className="text-sm text-muted-foreground">
          {t('settings.adoption.hint')}
        </p>
        <p id="adopted-on-error" role="alert" className="text-sm text-destructive empty:hidden">
          {fieldError}
        </p>
      </div>

      <Button type="submit" className="h-11 self-start" disabled={setAdoption.isPending}>
        {t(setAdoption.isPending ? 'settings.adoption.saving' : 'settings.adoption.save')}
      </Button>
    </form>
  )
}

// Server errors are translated by their stable key (ARCHITECTURE API errors).
function errorMessage(t: (key: string) => string, error: unknown) {
  const key = (error as ApiError | undefined)?.error?.key
  switch (key) {
    case 'adoption_date_locked':
      return t('settings.adoption.locked')
    case 'validation_failed':
      return t('settings.adoption.invalid')
    default:
      return t('errors.generic')
  }
}
